/**
 * IPC register: payment applications across all EPC contracts, for the dedicated IPC (Quantity Surveyor) users.
 */
import React, { useMemo, useState } from 'react';
import { Button, Card, Form, Input, Modal, Select, Space, Statistic, Table, Tag, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { epcContractAdminService } from '@/services/epcContractAdmin.service';
import { epcCommercialService } from '@/services/epcCommercial.service';
import { useAuthStore } from '@/store/authStore';
import { formatMoney } from '@/utils/format';

const { Title, Text } = Typography;

export const IPC_STATUSES = ['Draft', 'Submitted', 'Approved', 'Rejected', 'Certified'];
const IPC_CREATE_PERMISSIONS = ['epc.payment_application.manage', 'epc.contract_admin.manage', 'epc.contract.manage', 'epc.admin'];
const CONTRACT_BILLABLE_STATUSES = ['Approved', 'Active'];

function statusColor(s: string) {
  if (s === 'Approved' || s === 'Certified') return 'green';
  if (s === 'Submitted') return 'blue';
  if (s === 'Rejected') return 'red';
  return undefined;
}

const EpcIpcRegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canCreate = !!user?.is_super_admin || IPC_CREATE_PERMISSIONS.some((p) => user?.permissions?.includes(p));
  const [status, setStatus] = useState<string | undefined>();
  const [search, setSearch] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickedContract, setPickedContract] = useState<string | undefined>();

  const listQ = useQuery({
    queryKey: ['epc-ipc-register', status],
    queryFn: () => epcContractAdminService.listPaymentApplications(undefined, { status }),
    staleTime: 15_000,
  });
  const contractsQ = useQuery({
    queryKey: ['epc-contracts'],
    queryFn: () => epcCommercialService.listContracts(),
    enabled: pickerOpen,
    staleTime: 30_000,
  });

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const all = (listQ.data || []) as Record<string, unknown>[];
    if (!q) return all;
    return all.filter((r) =>
      [r.application_number, r.contract_number, r.contract_title, r.customer_name]
        .map((v) => String(v || '').toLowerCase())
        .some((v) => v.includes(q))
    );
  }, [listQ.data, search]);

  const totals = useMemo(() => {
    const sum = (key: string) => rows.reduce((s, r) => s + (Number(r[key]) || 0), 0);
    return { gross: sum('gross_applied_amount'), net: sum('net_applied_amount') };
  }, [rows]);

  const billableContracts = ((contractsQ.data || []) as Record<string, unknown>[]).filter((c) =>
    CONTRACT_BILLABLE_STATUSES.includes(String(c.status))
  );
  const openPath = (r: Record<string, unknown>) =>
    `/epc/commercial/contracts/${String(r.contract_id)}/payment-applications/${String(r.id)}`;

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }} align="start">
        <Title level={3} style={{ marginBottom: 4 }}>
          Payment Applications (IPC)
        </Title>
        {canCreate ? (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setPickerOpen(true)}>
            New IPC
          </Button>
        ) : null}
      </Space>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Interim payment applications across all contracts. Draft → Submit → Approve → Certify (by the commercial
        approver) → Draft Progress Tax Invoice. Base each line's this-period % on site progress or attached measurement
        evidence.
      </Text>
      <Card>
        <Space wrap style={{ marginBottom: 12 }}>
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 160 }}
            value={status}
            onChange={setStatus}
            options={IPC_STATUSES.map((s) => ({ value: s, label: s }))}
          />
          <Input.Search
            allowClear
            placeholder="IPC, contract or customer"
            style={{ width: 280 }}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Statistic title="Gross applied" value={totals.gross} precision={2} valueStyle={{ fontSize: 16 }} />
          <Statistic title="Net applied" value={totals.net} precision={2} valueStyle={{ fontSize: 16 }} />
        </Space>
        <Table
          size="small"
          loading={listQ.isLoading}
          rowKey={(r) => String(r.id)}
          dataSource={rows}
          pagination={{ pageSize: 25 }}
          scroll={{ x: 1100 }}
          onRow={(r) => ({ onClick: () => navigate(openPath(r)), style: { cursor: 'pointer' } })}
          columns={[
            {
              title: 'IPC',
              dataIndex: 'application_number',
              width: 140,
              render: (v, r) => (
                <Link to={openPath(r)} onClick={(e) => e.stopPropagation()}>
                  {String(v || '—')}
                </Link>
              ),
            },
            {
              title: 'Contract',
              dataIndex: 'contract_number',
              width: 130,
              render: (v, r) => (
                <Link to={`/epc/commercial/contracts/${String(r.contract_id)}`} onClick={(e) => e.stopPropagation()}>
                  {String(v || '—')}
                </Link>
              ),
            },
            { title: 'Contract title', dataIndex: 'contract_title', ellipsis: true },
            { title: 'Customer', dataIndex: 'customer_name', width: 200, ellipsis: true },
            {
              title: 'Period',
              key: 'period',
              width: 190,
              render: (_: unknown, r) =>
                `${r.period_from ? String(r.period_from).slice(0, 10) : '—'} → ${r.period_to ? String(r.period_to).slice(0, 10) : '—'}`,
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 110,
              render: (s: string) => <Tag color={statusColor(s)}>{s}</Tag>,
            },
            {
              title: 'Gross applied',
              dataIndex: 'gross_applied_amount',
              width: 140,
              align: 'right' as const,
              render: (v, r) => formatMoney(v, { currency: String(r.currency || 'AED') }),
            },
            {
              title: 'Net applied',
              dataIndex: 'net_applied_amount',
              width: 140,
              align: 'right' as const,
              render: (v, r) => formatMoney(v, { currency: String(r.currency || 'AED') }),
            },
          ]}
        />
      </Card>

      <Modal
        title="New IPC — select contract"
        open={pickerOpen}
        okText="Continue"
        okButtonProps={{ disabled: !pickedContract }}
        onCancel={() => setPickerOpen(false)}
        onOk={() => {
          if (!pickedContract) return;
          setPickerOpen(false);
          navigate(`/epc/commercial/contracts/${pickedContract}/payment-applications/new`);
        }}
        destroyOnClose
      >
        <Form layout="vertical">
          <Form.Item label="Contract (Approved or Active)" required>
            <Select
              showSearch
              loading={contractsQ.isLoading}
              placeholder="Select contract"
              optionFilterProp="label"
              value={pickedContract}
              onChange={setPickedContract}
              options={billableContracts.map((c) => ({
                value: String(c.id),
                label: `${String(c.contract_number || '')} — ${String(c.title || '')}${c.customer_name ? ` (${String(c.customer_name)})` : ''}`,
              }))}
              notFoundContent={contractsQ.isLoading ? 'Loading…' : 'No Approved or Active contracts'}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EpcIpcRegisterPage;
